import React, { useEffect, useState, useRef } from 'react';
import { TextAreaField } from './TextAreaField';
import { useNode, type Node } from '@craftjs/core';
import type { TextProps } from '@builder4/shared/components/Text';
import { editorRegistry } from '@/components/userComponents/Text';
import { Transforms, Editor, type Descendant } from 'slate';
import { slateValueToText } from '@/utils/helperFunc';
import { applyTextDiffToEditor } from '@/utils/textUtils';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT } from '@/analytics/mixpanel';

const createEmptySlateValue = (): Descendant[] => [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const createPlainTextSlateValue = (text: string): Descendant[] => [
  {
    type: 'paragraph',
    children: [{ text }],
  },
];

const NodeTextAreaSettings = () => {
  const {
    actions: { setProp },
    textStyles,
    id,
  } = useNode((node: Node) => ({
    id: node.id,
    textStyles:
      typeof node.data.props.textStyles === 'string'
        ? JSON.parse(node.data.props.textStyles)
        : node.data.props.textStyles || [],
  }));
  const [value, setValue] = useState<string>(slateValueToText(textStyles));
  const originalValueRef = useRef<string>(slateValueToText(textStyles));
  const track = useMixpanel();

  useEffect(() => {
    const text = slateValueToText(textStyles);
    if (value !== text) {
      setValue(text);
      originalValueRef.current = text;
    }
  }, [textStyles]);

  // Helper function to update props
  const updateProps = (slateValue: Descendant[]) => {
    setProp((props: Partial<TextProps>) => {
      props.textStyles = slateValue;
    }, 500);
  };

  // Helper function to clear slate editor
  const clearSlateEditor = (editor: Editor) => {
    Editor.withoutNormalizing(editor, () => {
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
      Transforms.delete(editor);

      if (editor.children.length === 0) {
        Transforms.insertNodes(editor, createEmptySlateValue()[0]);
      }
    });
  };

  // Helper function to replace slate editor content
  const replaceSlateEditorContent = (editor: Editor, text: string) => {
    Editor.withoutNormalizing(editor, () => {
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
      Transforms.delete(editor);

      if (text) {
        Transforms.insertText(editor, text);
      } else {
        Transforms.insertNodes(editor, createEmptySlateValue()[0]);
      }
    });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const slateEditor = editorRegistry.get(id);

    // Update local state
    setValue(newValue);

    const editor = slateEditor?.editor;

    // Handle empty value
    if (newValue === '') {
      if (editor) {
        // Slate operations trigger onChange → useSlateTextStyles.handleChange → setProp
        clearSlateEditor(editor);
      } else {
        updateProps(createEmptySlateValue());
      }
      return;
    }

    // Use the Slate editor's actual text as the diff base to avoid stale
    // textStyles (which are updated on a throttle via setProp).
    const currentText = editor
      ? slateValueToText(editor.children as Descendant[])
      : slateValueToText(textStyles ?? []);

    if (currentText === newValue) return;

    if (editor) {
      // Apply diff to preserve existing marks/styling.
      // Do NOT call updateProps after modifying the editor — Slate's onChange
      // fires synchronously and useSlateTextStyles.handleChange persists via
      // immediate setProp. A redundant throttled updateProps would overwrite
      // the latest textStyles with stale content, causing style resets.
      const applied = applyTextDiffToEditor(editor, currentText, newValue);
      if (applied) return;

      // Diff failed — replace content entirely (loses marks)
      replaceSlateEditorContent(editor, newValue);
      return;
    }

    // No editor available — persist plain text directly
    updateProps(createPlainTextSlateValue(newValue));
  };

  const handleOnBlur = () => {
    if (value !== originalValueRef.current) {
      track(EVENT.TEXT_EDITED);
      originalValueRef.current = value;
    }
  };

  return (
    <TextAreaField
      inputValue={value}
      className="max-w-[216px]"
      placeholderText=""
      text="Content"
      onChange={handleOnChange}
      onBlur={handleOnBlur}
    />
  );
};

export default NodeTextAreaSettings;
